/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn, hashTagRegexFactory, linkRegexFactory } from 'common/utils';
import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  DraftHandleValue,
  Editor,
  EditorState,
  getDefaultKeyBinding,
} from 'draft-js';

import 'draft-js/dist/Draft.css';

import { useCallback } from 'react';
import { User } from 'common/interfaces/api';
import ErrorBoundaryWithHandler from 'common/utils/ErrorBoundaryClient';
import { Modifier } from 'draft-js';
import { BlackListUrl, PolicyError } from 'service/post';

import styles from './TextEditor.module.scss';

function findWithRegex(
  regex: RegExp,
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
) {
  const text = contentBlock.getText();
  let matchArr: RegExpExecArray;
  let start: number;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
}

const mentionSpan = ({ children }) => {
  return <span className={styles.mention}>{children}</span>;
};

const hashtagSpan = ({ children }) => {
  return <span className={styles.hashtag}>{children}</span>;
};

const OverLimitSpan = ({ children }) => {
  return <span className={styles.overLimit}>{children}</span>;
};

const hashtagRegedx = hashTagRegexFactory();
const linkRegex = linkRegexFactory();

function hashtagStrategy(contentBlock, callback) {
  findWithRegex(hashtagRegedx, contentBlock, callback);
}

function linkStrategy(contentBlock, callback) {
  findWithRegex(linkRegex, contentBlock, callback);
}

export type TextEditorRef = {
  getContent: () => string;
  setEditorText: (s: string) => void;
  focus: () => void;
  reset: () => void;
  forceRender: () => void;
  insertText: (text: string) => void;
};

export type Props = {
  setPlainText: (text: string) => void;
  placeholder?: string;
  onFocus?: () => void;
  readOnly?: boolean;
  initialText?: string;
  charLimit?: number;
  mentionTarget?: User;
  textPolicies?: PolicyError[];
  blackUrls?: BlackListUrl[];
  onEnter?: () => void;
  className?: string;
  singleLine?: boolean;
};

const TextEditor = (
  {
    setPlainText,
    placeholder,
    onFocus,
    readOnly,
    initialText = '',
    charLimit = 2000,
    mentionTarget,
    textPolicies = [],
    blackUrls = [],
    onEnter,
    className,
    singleLine = false,
  }: Props,
  ref: React.Ref<TextEditorRef>,
): JSX.Element => {
  const overLimitStrategy = useMemo(() => {
    return (
      contentBlock: ContentBlock,
      callback: (start: number, end: number) => void,
    ) => {
      const text = contentBlock.getText();
      const len = text.length;
      if (len > charLimit) {
        callback(charLimit, len);
      }
    };
  }, [charLimit]);

  const mentionStrategy = useMemo(() => {
    return (
      contentBlock: ContentBlock,
      callback: (start: number, end: number) => void,
    ) => {
      if (!mentionTarget) return;
      const text = contentBlock.getText();
      try {
        const regex = new RegExp(
          `@${mentionTarget.name}`
            .replaceAll('(', '\\(')
            .replaceAll(')', '\\)')
            .replaceAll('[', '\\[')
            .replaceAll('*', '\\*')
            .replaceAll('+', '\\+'),
        );
        const matchArr = regex.exec(text);
        if (matchArr !== null) {
          const start = matchArr.index;
          callback(start, start + matchArr[0].length);
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    };
  }, [mentionTarget]);

  const policyStrategy = useMemo(() => {
    return (
      contentBlock: ContentBlock,
      callback: (start: number, end: number) => void,
    ) => {
      if (!textPolicies || textPolicies.length === 0) return;
      const text = contentBlock.getText();
      try {
        textPolicies.forEach((policy) => {
          const regex = new RegExp(policy.name);
          const matchArr = regex.exec(text);
          if (matchArr !== null) {
            const start = matchArr.index;
            callback(start, start + matchArr[0].length);
          }
        });
        // eslint-disable-next-line no-empty
      } catch (e) {}
    };
  }, [textPolicies]);

  const policySpan = useCallback((data: any) => {
    const children = data.children;
    return (
      <span className={styles.policy}>
        <del>{children}</del>
      </span>
    );
  }, []);

  const linkSpan = useCallback(
    (data: any) => {
      const children = data.children;
      if (!blackUrls || blackUrls.length === 0) {
        return <span className={styles.link}>{children}</span>;
      }
      const text = data.decoratedText;
      const blackUrl = blackUrls.find((u) => u.url.includes(text));
      if (!blackUrl) {
        return <span className={styles.link}>{children}</span>;
      }
      return (
        <span className={styles.policy}>
          <del>{children}</del>
        </span>
      );
    },
    [blackUrls],
  );

  const decorator = useMemo(
    () =>
      new CompositeDecorator([
        {
          strategy: overLimitStrategy,
          component: OverLimitSpan,
        },
        {
          strategy: linkStrategy,
          component: linkSpan,
        },
        {
          strategy: mentionStrategy,
          component: mentionSpan,
        },
        {
          strategy: policyStrategy,
          component: policySpan,
        },
        {
          strategy: hashtagStrategy,
          component: hashtagSpan,
        },
      ]),
    [overLimitStrategy, linkSpan, mentionStrategy, policyStrategy, policySpan],
  );

  const [editorState, setEditorState] = useState<EditorState>(() =>
    EditorState.createWithContent(
      ContentState.createFromText(initialText),
      decorator,
    ),
  );
  const selfRef = useRef<Editor>();

  useImperativeHandle(
    ref,
    () => ({
      getContent() {
        return editorState.getCurrentContent().getPlainText('');
      },
      setEditorText: (s: string) => {
        try {
          const currentContent = editorState.getCurrentContent();
          const selectAll = editorState.getSelection().merge({
            anchorKey: currentContent.getFirstBlock().getKey(),
            anchorOffset: 0,
            focusKey: currentContent.getLastBlock().getKey(),
            focusOffset: currentContent.getLastBlock().getLength(),
          });

          const newContentState = Modifier.replaceText(
            currentContent,
            selectAll,
            s,
          );

          const newEditorState = EditorState.push(
            editorState,
            newContentState,
            'insert-characters',
          );

          setEditorState(newEditorState);
        } catch (error) {
          const state = EditorState.set(
            EditorState.createWithContent(ContentState.createFromText(s)),
            { decorator },
          );
          setEditorState(state);
        }
      },
      focus() {
        setEditorState(EditorState.moveFocusToEnd(editorState));
      },
      reset() {
        const newState = EditorState.createEmpty();
        setEditorState(EditorState.moveFocusToEnd(newState));
      },
      insertText: (text: string) => {
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const newContentState = Modifier.insertText(
          contentState,
          selectionState,
          text,
        );
        setEditorState(
          EditorState.push(editorState, newContentState, 'insert-characters'),
        );
      },
      forceRender: function () {
        const content = editorState.getCurrentContent();
        const currentOffset = editorState.getSelection().getFocusOffset();
        const newEditorState = EditorState.createWithContent(
          content,
          decorator,
        );
        const newSelection = editorState.getSelection().merge({
          focusOffset: currentOffset,
          anchorOffset: currentOffset,
        });
        setEditorState(
          EditorState.acceptSelection(newEditorState, newSelection),
        );
      },
    }),
    [editorState, decorator],
  );

  useEffect(() => {
    const plainText = editorState.getCurrentContent().getPlainText('');
    setPlainText(plainText);
  }, [setPlainText, editorState]);

  const handleBeforeInput = (
    chars: string,
    state: EditorState,
  ): DraftHandleValue => {
    try {
      const currentContentState = state.getCurrentContent();
      const selectionState = state.getSelection();
      setEditorState(
        EditorState.push(
          editorState,
          Modifier.replaceText(currentContentState, selectionState, chars),
          'remove-range',
        ),
      );

      // eslint-disable-next-line no-empty
    } catch (error) {}
    return 'handled';
  };

  const handleKeyCommand = (command: string): DraftHandleValue => {
    if (command === 'enter_command') {
      onEnter && onEnter();
      return 'handled';
    }

    if (command === 'split-block' && singleLine) {
      // Prevent new line when singleLine is true
      return 'handled';
    }

    return 'not-handled';
  };

  const myKeyBindingFn = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && onEnter) {
      return 'enter_command';
    }

    if (e.key === 'Enter' && singleLine) {
      // Prevent default Enter behavior for single line
      return 'split-block';
    }

    return getDefaultKeyBinding(e);
  };

  return (
    <div className={cn(styles.textEditorWrapper, className ?? '')}>
      <ErrorBoundaryWithHandler
        errorHandler={() => {
          const content = editorState.getCurrentContent();
          const currentOffset = editorState.getSelection().getFocusOffset();
          const newEditorState = EditorState.createWithContent(
            content,
            decorator,
          );
          const newSelection = editorState.getSelection().merge({
            focusOffset: currentOffset,
            anchorOffset: currentOffset,
          });
          setEditorState(
            EditorState.acceptSelection(newEditorState, newSelection),
          );
        }}
      >
        {editorState && (
          <Editor
            ref={selfRef}
            editorState={editorState}
            onChange={setEditorState}
            placeholder={placeholder}
            spellCheck={true}
            handleKeyCommand={handleKeyCommand}
            keyBindingFn={myKeyBindingFn}
            onFocus={onFocus}
            readOnly={readOnly}
            handleBeforeInput={handleBeforeInput}
            handlePastedText={(text) => {
              try {
                let processedText = text;

                // If single line, remove line breaks
                if (singleLine) {
                  processedText = text.replace(/\r?\n|\r/g, ' ');
                }

                const pastedBlocks =
                  ContentState.createFromText(processedText).getBlockMap();
                const newState = Modifier.replaceWithFragment(
                  editorState.getCurrentContent(),
                  editorState.getSelection(),
                  pastedBlocks,
                );
                setEditorState(
                  EditorState.push(editorState, newState, 'insert-fragment'),
                );
                // eslint-disable-next-line no-empty
              } catch (error) {}
              return 'handled';
            }}
          />
        )}
      </ErrorBoundaryWithHandler>
    </div>
  );
};

// const policyLink = (text: string, url: string): string => {
//   const start = text.indexOf(`[`);
//   const end = text.indexOf(`]`);
//   const linkText = text.substring(start + 1, end);
//   return text.replace(
//     `[${linkText}]`,
//     `<a onclick="window.open('${url}')">${linkText}</a>`
//   );
// };

export default forwardRef(TextEditor);
